import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adrift as featuredBook } from '../data/books';
import useAdriftAvailability from '../hooks/useAdriftAvailability';
import './HomePage.css';
import '../pages/MusicPage.css'; // Reuse streaming button colors

const primaryRelease = {
  title: 'Byron Schmyron',
  year: 2026,
  image: '/Byron_Schmyron.png',
  tagline: 'A left-turn pop ride with sharp hooks and a restless pulse.',
  streamingLinks: [
    { platform: 'Spotify', url: 'https://open.spotify.com/album/0TirZ3EBfFnhIrPQwFVSNN?si=OJTFn1AKTe-8gIsU-BSB-A' },
    { platform: 'AppleMusic', url: 'https://music.apple.com/us/artist/d-byron/1700392730' },
    { platform: 'YouTube', url: 'https://www.youtube.com/watch?v=uQ-RdJhZs7g&list=OLAK5uy_k9pGZyJv1JJLnS4v8pXAm2GRksUCP7HOM' },
    { platform: 'Pandora', url: 'https://www.pandora.com/artist/d-byron/byron-schmyron/ALnm9vdkzph22PJ' },
    { platform: 'iHeart', url: 'https://www.iheart.com/artist/d-byron-40939167/albums/byron-schmyron-369425584' }
  ]
};

const followLinks = [
  { platform: 'Spotify', url: 'https://open.spotify.com/artist/3RYb0Sn2fzlMVTnES9xtGp' },
  { platform: 'AppleMusic', url: 'https://music.apple.com/us/artist/d-byron/1700392730' },
  { platform: 'YouTube', url: 'https://www.youtube.com/@DByronMusician' },
  { platform: 'Pandora', url: 'https://www.pandora.com/artist/d-byron/ARxtvlrkb3Vd4pP' },
  { platform: 'iHeart', url: 'https://www.iheart.com/artist/d-byron-40939167/' }
];

const catalogHighlights = [
  {
    title: 'File Under Unheard',
    year: 2024,
    image: '/fileunderunheard.png',
    flash: false,
    streamingLinks: [
      { platform: 'Spotify', url: 'https://open.spotify.com/album/4l9OhH1Fu25MvNcaZqkTXz' },
      { platform: 'AppleMusic', url: 'https://music.apple.com/us/album/file-under-unheard/1778154896' },
      { platform: 'YouTube', url: 'https://www.youtube.com/playlist?list=OLAK5uy_lHG668dgILBt3Ee5KOw-TpNo9VA98E6VE' }
    ]
  },
  {
    title: 'Byron Schmyron',
    year: 2026,
    image: '/Byron_Schmyron.png',
    flash: false,
    streamingLinks: [
      { platform: 'Spotify', url: 'https://open.spotify.com/album/0TirZ3EBfFnhIrPQwFVSNN?si=OJTFn1AKTe-8gIsU-BSB-A' },
      { platform: 'AppleMusic', url: 'https://music.apple.com/us/artist/d-byron/1700392730' },
      { platform: 'YouTube', url: 'https://www.youtube.com/watch?v=uQ-RdJhZs7g&list=OLAK5uy_k9pGZyJv1JJLnS4v8pXAm2GRksUCP7HOM' },
      { platform: 'Pandora', url: 'https://www.pandora.com/artist/d-byron/byron-schmyron/ALnm9vdkzph22PJ' },
      { platform: 'iHeart', url: 'https://www.iheart.com/artist/d-byron-40939167/albums/byron-schmyron-369425584' }
    ]
  }
];

const getPlatformClass = (platform) => platform.toLowerCase().replace(' ', '-');
const getLocalDayIndex = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return Math.floor((Date.UTC(year, month, day) - Date.UTC(year, 0, 1)) / 86400000);
};

function HomePage() {
  const adriftAvailable = useAdriftAvailability();
  const [dailyHighlight, setDailyHighlight] = useState(() => {
    if (catalogHighlights.length === 0) {
      return null;
    }
    const dayIndex = getLocalDayIndex(new Date());
    return catalogHighlights[dayIndex % catalogHighlights.length];
  });

  useEffect(() => {
    if (catalogHighlights.length === 0) {
      return undefined;
    }

    let timeoutId;

    const updateHighlight = () => {
      const dayIndex = getLocalDayIndex(new Date());
      setDailyHighlight(catalogHighlights[dayIndex % catalogHighlights.length]);
    };

    const scheduleNextUpdate = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const msUntilMidnight = nextMidnight - now;
      timeoutId = setTimeout(() => {
        updateHighlight();
        scheduleNextUpdate();
      }, msUntilMidnight);
    };

    scheduleNextUpdate();

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="home-page">
      <main>
        <section className="hero">
          <div className="hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">Current release</p>
              <h2>{primaryRelease.title}</h2>
              <p className="hero-tagline">{primaryRelease.tagline}</p>
              <div className="hero-cta">
                {primaryRelease.streamingLinks.map((link) => (
                  <a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn chip ${getPlatformClass(link.platform)}`}
                  >
                    {link.platform}
                  </a>
                ))}
                <Link to="/music" className="btn ghost-btn">
                  See the full catalog
                </Link>
              </div>
            </div>
            <div className="hero-cover">
              <div className="cover-frame">
                <img src={primaryRelease.image} alt={`${primaryRelease.title} cover`} />
              </div>
            </div>
          </div>
        </section>

        <section className="presence-strip">
          <div className="presence-text">
            Follow, stream, or reach out. This is the front door.
          </div>
          <div className="presence-links">
            {followLinks.map((link) => (
              <a
                key={link.platform}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn chip ${getPlatformClass(link.platform)}`}
              >
                {link.platform}
              </a>
            ))}
            <Link to="/contact" className="btn chip ghost-btn">
              Press / Contact
            </Link>
          </div>
        </section>

        <section className="feature-panels">
          <div className="panel book-panel">
            <div className="panel-media">
              <img src={featuredBook.image} alt={`${featuredBook.title} cover`} />
            </div>
            <div className="panel-body">
              <p className="eyebrow">{adriftAvailable ? 'Latest novel' : 'Upcoming novel'}</p>
              <h3>{featuredBook.title}</h3>
              <p className="release-date">{adriftAvailable ? 'Available now' : 'Coming November 6, 2026'}</p>
              <p>{featuredBook.note}</p>
              <div className="panel-actions">
                  <a
                    href={featuredBook.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Buy Now
                  </a>
              </div>
            </div>
          </div>

          <div className="panel music-panel">
            <div className="panel-heading">
              <p className="eyebrow">Also spinning</p>
              <h3>Catalog highlight</h3>
            </div>
            <div className="panel-grid">
              {dailyHighlight && (
                <div className="mini-album">
                  <img src={dailyHighlight.image} alt={`${dailyHighlight.title} cover`} />
                  <div className="mini-album-info">
                    <h4>{dailyHighlight.title} ({dailyHighlight.year})</h4>
                    {dailyHighlight.purchaseLink && (
                      <a
                        href={dailyHighlight.purchaseLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`btn elastic-stage ${dailyHighlight.flash ? 'flash-button' : ''}`}
                      >
                        {dailyHighlight.purchaseText}
                      </a>
                    )}
                    {dailyHighlight.streamingLinks.length > 0 && (
                      <div className="mini-streaming-links">
                        {dailyHighlight.streamingLinks.map((link) => (
                          <a
                            key={link.platform}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`btn chip ${getPlatformClass(link.platform)}`}
                          >
                            {link.platform}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default HomePage;
